import ApiBase, { statuses } from '../lib/ApiBase';
import { getAccessToken, getRefreshToken, saveTokens } from './localStorage';
import { captureError, isTokenExpiredOrAboutToExpire } from '../lib/utils';
import logOut from './session';
import { getApiDomain } from './countryBasedConfig';

interface AuthenticationResultResponse {
	accessToken: string;
	refreshToken: string;
}

// This global is used to prevent multiple concurrent ajax requests from each trying to generate a new access token
// when the access token expires.
let generateAccessTokenPromise: Promise<Response> = null;

/**
 * @description Defines the basic interface to the API
 */
export default class AppApi extends ApiBase {
	constructor() {
		super(getApiDomain());
	}

	// eslint-disable-next-line class-methods-use-this
	async getAccessToken(): Promise<string> {
		const accessToken = await AppApi.getOrGenerateAccessToken();
		if (accessToken === null) {
			logOut(true);
			await new Promise(() => {});
			// Awaiting a never-resolved promise to effectively stop execution so that this fn's promise won't
			// resolve prior to navigation to login.
		}
		return accessToken;
	}

	public static async getOrGenerateAccessToken() {
		const accessToken = getAccessToken();

		// If there's no access token at all, don't try to refresh - just return null
		if (!accessToken) {
			return null;
		}

		if (isTokenExpiredOrAboutToExpire(accessToken)) {
			// eslint-disable-next-line no-case-declarations
			const authResult = await this.generateAccessToken(getRefreshToken(), accessToken);
			if (authResult === null) {
				return null;
			}
			saveTokens(authResult.refreshToken, authResult.accessToken);
			return authResult.accessToken;
		}
		return accessToken;
	}

	public static async generateAccessToken(
		refreshToken: string,
		accessToken: string,
		retryCount = 0
	): Promise<AuthenticationResultResponse | null> {
		// If refresh token is missing or invalid, don't attempt the API call
		if (!refreshToken || !accessToken) {
			return null;
		}

		const url = `${getApiDomain()}api/Authentication/AccessToken`;
		const body = JSON.stringify({ refreshToken, accessToken });
		const headers: Record<string, string> = {
			'content-type': 'application/json',
			accept: 'application/json'
		};

		try {
			if (generateAccessTokenPromise === null) {
				generateAccessTokenPromise = fetch(url, { method: 'POST', body, headers });
			}

			// The response must be cloned as this task can be awaited on by multiple ajax requests.
			const response = (await generateAccessTokenPromise).clone();
			generateAccessTokenPromise = null;

			if (response.status === statuses.ok) {
				const result: AuthenticationResultResponse = await response.json();
				return {
					accessToken: result.accessToken,
					refreshToken: result.refreshToken
				};
			}
			if (response.status === statuses.unauthorized || response.status === statuses.badRequest) {
				// Treat 401 Unauthorized and 400 Bad Request (invalid refresh token) the same - trigger logout
				return null;
			}
			// For any other status code (500, 503, etc.), log error and trigger logout
			captureError(new Error(`Unexpected status code ${response.status} from AccessToken endpoint`), (scope) => {
				scope.setTag('access-token-generation', 'generateAccessToken');
				scope.setContext('response', { status: response.status });
			});
			return null;
		} catch (error) {
			// Network error or other fetch failure - reset promise and retry before giving up
			generateAccessTokenPromise = null;
			if (retryCount < 2) {
				await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
				return AppApi.generateAccessToken(refreshToken, accessToken, retryCount + 1);
			}
			captureError(error, (scope) => {
				scope.setTag('access-token-generation', 'generateAccessToken');
				scope.setContext('error', { type: 'network-or-fetch-error' });
			});
			return null;
		}
	}
}
